import styled from 'styled-components';
import getStatusDataByServiceType from '../components/UtilComponents/statusType';
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(localizedFormat);
dayjs.locale('de');

const RestaurantStatusWrapper = styled.div`
  display: block;
  position: relative;
  bottom: 0;
  color: #ffffff;
  text-align: center;

  background-color: #ec971f;
  z-index: 2;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;

  p {
    margin: 0;
    line-height: 19px;
    font-size: 15px;
    letter-spacing: 0.67px;

    font-weight: bold;
    @media (min-width: 768px) {
      line-height: 25px;
      font-size: 19px;
      letter-spacing: 0.84px;
    }
  }

  span {
    margin: 0;
    line-height: 19px;
    font-size: 15px;
    letter-spacing: 0.67px;
    @media (min-width: 768px) {
      line-height: 25px;
      font-size: 19px;
      letter-spacing: 0.84px;
    }
  }

  margin-left: -15px;
  margin-right: -15px;
  padding-bottom: 5px;
  padding-top: 5px;
  padding-left: 5px;
  padding-right: 5px;
  margin-bottom: 0;

  @media (min-width: 768px) {
    padding-bottom: 5px;
    padding-top: 5px;
    padding-left: 55px;
    padding-right: 55px;

    margin-left: -55px;
    margin-right: -55px;
    margin-bottom: 0;
  }
`;

const getIndividualStatusText = (statusData, serviceName) => {
  let heading = '';
  let subtitle = '';

  switch (statusData.status) {
    case 'CLOSED_WITHOUT_PREORDER':
    case 'INACTIVE_OUTSIDE_OPERATING_HOURS':
      heading = `${serviceName} aktuell geschlossen`;
      subtitle = 'Besuchen Sie uns gerne wieder zu unseren Öffnungszeiten';
      break;
    case 'CLOSED_WITH_PREORDER':
      heading = `${serviceName} noch geschlossen`;
      subtitle = `Wir öffnen um ${dayjs(statusData.payload.firstMoment).format(
        'LT',
      )} Uhr. Vorbestellungen möglich.`;
      break;
    case 'INACTIVE_IN_OPERATING_HOURS':
      heading = `${serviceName} vorübergehend deaktiviert`;
      subtitle = 'Bitte versuchen Sie es später noch einmal';
      break;
    case 'HOLIDAYS':
      heading = `Wir sind aktuell im Urlaub`;
      subtitle = `Ab dem ${dayjs(statusData.payload.dateTo)
        .add(1, 'day')
        .format('L')} sind wir wieder zurück`;
      break;
    case 'OFFLINE_IN_OPERATING_HOURS':
      heading = `${serviceName} aktuell offline`;
      subtitle = 'Bitte versuchen Sie es später noch einmal';
      break;
    default:
      heading = `${serviceName} geschlossen`;
      subtitle = '';
      break;
  }

  return (
    <>
      <p>{heading}</p>
      {subtitle && <span>{subtitle}</span>}
    </>
  );
};

const areHashesEqual = (storeProps) => {
  const operatingHours = storeProps.operatingHours || {};
  const deliveryHash = operatingHours.deliveryHoursHash;
  const openingHash = operatingHours.openingHoursHash;
  return (
    typeof deliveryHash === 'string' &&
    typeof openingHash === 'string' &&
    deliveryHash === openingHash
  );
};

export function getServiceStatus(restaurantProps, serviceType) {
  const statusData = getStatusDataByServiceType(restaurantProps, serviceType);
  return statusData.status;
}

/**
 * This function determines the status text to be displayed:
 * - If at least one of the services (Delivery or Pickup) has the status OPEN, zero is returned.
 * - If at least one is offline, a uniform offline message is output.
 * Otherwise, either a combined (both equal) or two separate status lines are output based on the hash comparisons.
 */
const getRestaurantStatusText = (storeProps, withWrapper = true) => {
  const deliveryStatusData = storeProps.deliveryStatusData;
  const pickupStatusData = storeProps.pickUpStatusData;

  if (deliveryStatusData.status === 'OPEN' || pickupStatusData.status === 'OPEN') {
    return null;
  }

  if (
    deliveryStatusData.status === 'OFFLINE_IN_OPERATING_HOURS' ||
    pickupStatusData.status === 'OFFLINE_IN_OPERATING_HOURS'
  ) {
    return withWrapper ? (
      <RestaurantStatusWrapper>
        <p>Restaurant aktuell offline</p>
        <span>Bitte versuchen Sie es später noch einmal</span>
      </RestaurantStatusWrapper>
    ) : (
      <>
        <p>Restaurant aktuell offline</p>
        <span>Bitte versuchen Sie es später noch einmal</span>
      </>
    );
  }
  if (deliveryStatusData.status === 'HOLIDAYS' || pickupStatusData.status === 'HOLIDAYS') {
    return withWrapper ? (
      <RestaurantStatusWrapper>
        <p>Wir sind aktuell im Urlaub</p>
        <span>{`Ab dem ${dayjs(deliveryStatusData.payload.dateTo)
          .add(1, 'day')
          .format('L')} sind wir wieder zurück`}</span>
      </RestaurantStatusWrapper>
    ) : (
      <>
        <p>Wir sind aktuell im Urlaub</p>
        <span>{`Ab dem ${dayjs(deliveryStatusData.payload.dateTo)
          .add(1, 'day')
          .format('L')} sind wir wieder zurück`}</span>
      </>
    );
  }
  if (
    (deliveryStatusData.status === 'CLOSED_WITH_PREORDER' &&
      pickupStatusData.status === 'CLOSED_WITHOUT_PREORDER') ||
    (pickupStatusData.status === 'CLOSED_WITH_PREORDER' &&
      deliveryStatusData.status === 'CLOSED_WITHOUT_PREORDER')
  ) {
    const preorderStatus =
      deliveryStatusData.status === 'CLOSED_WITH_PREORDER' ? deliveryStatusData : pickupStatusData;
    return withWrapper ? (
      <RestaurantStatusWrapper>
        {getIndividualStatusText(preorderStatus, 'Restaurant')}
      </RestaurantStatusWrapper>
    ) : (
      getIndividualStatusText(preorderStatus, 'Restaurant')
    );
  }

  if (
    deliveryStatusData.status === 'CLOSED_WITHOUT_PREORDER' &&
    pickupStatusData.status === 'CLOSED_WITHOUT_PREORDER'
  ) {
    return withWrapper ? (
      <RestaurantStatusWrapper>
        <p>Wir haben aktuell geschlossen</p>
        <span>Besuchen Sie uns gerne wieder zu unseren Öffnungszeiten</span>
      </RestaurantStatusWrapper>
    ) : (
      <>
        <p>Wir haben aktuell geschlossen</p>
        <span>Besuchen Sie uns gerne wieder zu unseren Öffnungszeiten</span>
      </>
    );
  }

  if (areHashesEqual(storeProps)) {
    return withWrapper ? (
      <RestaurantStatusWrapper>
        {getIndividualStatusText(deliveryStatusData, 'Restaurant')}
      </RestaurantStatusWrapper>
    ) : (
      getIndividualStatusText(deliveryStatusData, 'Restaurant')
    );
  }
  if (
    deliveryStatusData.status === 'CLOSED_WITH_PREORDER' &&
    pickupStatusData.status === 'CLOSED_WITH_PREORDER'
  ) {
    return withWrapper ? (
      <>
        <RestaurantStatusWrapper>
          <p>Aktuell noch geschlossen</p>
          <span>
            Lieferung ab {dayjs(deliveryStatusData.payload.firstMoment).format('LT')} Uhr. Abholung
            ab {dayjs(pickupStatusData.payload.firstMoment).format('LT')} Uhr. Vorbestellungen
            möglich.
          </span>
        </RestaurantStatusWrapper>
      </>
    ) : (
      <>
        <p>Aktuell noch geschlossen</p>
        <span>
          Lieferung ab {dayjs(deliveryStatusData.payload.firstMoment).format('LT')} Uhr. Abholung ab{' '}
          {dayjs(pickupStatusData.payload.firstMoment).format('LT')} Uhr. Vorbestellungen möglich.
        </span>
      </>
    );
  }

  const isDeliveryInactiveInside = deliveryStatusData.status === 'INACTIVE_IN_OPERATING_HOURS';
  const isPickupInactiveInside = pickupStatusData.status === 'INACTIVE_IN_OPERATING_HOURS';

  if (isDeliveryInactiveInside && isPickupInactiveInside) {
    return withWrapper ? (
      <RestaurantStatusWrapper>
        <p>Lieferung & Abholung vorübergehend deaktiviert</p>
        <span>Bitte versuchen Sie es später noch einmal</span>
      </RestaurantStatusWrapper>
    ) : (
      <>
        <p>Lieferung & Abholung vorübergehend deaktiviert</p>
        <span>Bitte versuchen Sie es später noch einmal</span>
      </>
    );
  }

  const isDeliveryInactiveOutside =
    deliveryStatusData.status === 'INACTIVE_OUTSIDE_OPERATING_HOURS';
  const isPickupInactiveOutside = pickupStatusData.status === 'INACTIVE_OUTSIDE_OPERATING_HOURS';

  if (isDeliveryInactiveOutside && isPickupInactiveOutside) {
    return withWrapper ? (
      <RestaurantStatusWrapper>
        <p>Restaurant aktuell geschlossen</p>
        <span>Besuchen Sie uns gerne wieder zu unseren Öffnungszeiten</span>
      </RestaurantStatusWrapper>
    ) : (
      <>
        <p>Restaurant aktuell geschlossen</p>
        <span>Besuchen Sie uns gerne wieder zu unseren Öffnungszeiten</span>
      </>
    );
  }

  if (
    (isDeliveryInactiveOutside || isDeliveryInactiveInside) &&
    (isPickupInactiveOutside || isPickupInactiveInside)
  ) {
    return withWrapper ? (
      <RestaurantStatusWrapper>
        <p>Lieferung & Abholung vorübergehend deaktiviert</p>
        <span>Bitte versuchen Sie es später noch einmal</span>
      </RestaurantStatusWrapper>
    ) : (
      <>
        <p>Lieferung & Abholung vorübergehend deaktiviert</p>
        <span>Bitte versuchen Sie es später noch einmal</span>
      </>
    );
  }

  return withWrapper ? (
    <>
      <RestaurantStatusWrapper>
        {getIndividualStatusText(deliveryStatusData, 'Lieferung')}
      </RestaurantStatusWrapper>
      <RestaurantStatusWrapper>
        {getIndividualStatusText(pickupStatusData, 'Abholung')}
      </RestaurantStatusWrapper>
    </>
  ) : (
    <>
      {getIndividualStatusText(deliveryStatusData, 'Lieferung')}
      {getIndividualStatusText(pickupStatusData, 'Abholung')}
    </>
  );
};

export default getRestaurantStatusText;
